/** How a VIN reached the form. Mirrors VIN_SOURCES in the backend's utils/vin.ts. */
export type VinSource =
  | 'AI_REGISTRATION'   // استمارة uploaded on the order form, read by Gemini
  | 'AI_CHAT'           // the workshop AI assistant built the order
  | 'AI_WHATSAPP'       // a customer's WhatsApp message, parsed for a quote lead
  | 'PLATE_LOOKUP'      // pulled by plate from our records or Mize ERP
  | 'PROFILE'           // reused from a saved vehicle profile
  | 'PASTED'
  | 'TYPED';

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  AGENT = 'AGENT',
  WORKSHOP = 'WORKSHOP',
  SUPPLIER = 'SUPPLIER',
}

export enum OrderStatus {
  PENDING = 'PENDING',
  QUOTED = 'QUOTED',
  PR = 'PR',
  READY_FOR_PICKUP = 'READY_FOR_PICKUP',
  INVOICE_REQUEST = 'INVOICE_REQUEST',
  INVOICED = 'INVOICED',
  DELIVERED = 'DELIVERED',
  CANCELED = 'CANCELED',
}

export type WorkshopType = 'PRIVATE' | 'INDEPENDENT';

export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  workshopName?: string;
  workshopType?: WorkshopType;
  /** WORKSHOP logins: false = the parts catalog is hidden and refused for this workshop. */
  partsCatalogEnabled?: boolean;
  canCreateOrders?: boolean;
  approvedCarMakes?: string[];
  // Read-only auditor (ADMIN only): view-only, scoped to owned workshops.
  readOnly?: boolean;
  /** A workshop with its own brand (white-label, src/branding): the app wears it instead of Mize Pro. */
  brandKey?: string | null;
}

export interface Attachment {
  id: string;
  orderId: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  attachmentType: string;
  createdAt: string;
  uploadedBy: string;
  /** Purchase invoices: sent to Daftra (admins only). */
  erpPushedAt?: string | null;
  erpRef?: string | null;
}

export interface Payment {
  id: string;
  orderId: string;
  paymentType: 'CASH' | 'POS' | 'PAYMENT_LINK' | 'CREDIT';
  amount: number;
  customerPhone?: string;
  paymentLink?: string;
  paymentLinkExpiresAt?: string;
  paymentStatus?: 'SUCCESS' | 'FAILED' | 'DECLINED' | 'PENDING';
  attemptNumber?: number;
  declineReason?: string;
  webhookConfirmationId?: string;
  confirmedAt?: string;
  createdAt: string;
}

export interface Transfer {
  id: string;
  orderId: string;
  amountTransferred: number;
  proofOfTransfer?: string;
  transferredBy: string;
  transferredAt: string;
}

export interface OrderStatusHistory {
  id: string;
  orderId: string;
  oldStatus?: OrderStatus;
  newStatus: OrderStatus;
  changedAt: string;
  changedBy: string;
  duration?: number; // Duration in milliseconds
  reason?: string;
  changedByUser?: {
    id: string;
    username: string;
    role: UserRole;
  };
}

export interface EditHistory {
  id: string;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  editedBy: string;
  editedAt: string;
  comment?: string;
  editor?: {
    id: string;
    username: string;
    role: UserRole;
  };
}

/** Per-order delivery, computed by the server for the orders list. */
/** The most pressing state among an order's undelivered parts; DELIVERED only when every part is. */
export type OrderDeliveryState = 'NONE' | 'PREPARING' | 'READY' | 'IN_TRANSIT' | 'UNPROVEN' | 'DELIVERED';
export interface OrderDeliverySummary {
  total: number; delivered: number; inTransit: number; unproven: number; ready: number; preparing: number; returned: number;
  state: OrderDeliveryState; openRuns: string[]; drivers: string[]; unprovenRuns: string[]; lastDeliveredAt: string | null;
  /** The earliest signed-sheet deadline among this order's runs still with the driver. */
  dueAt: string | null;
}

/** Printed delivery notes («سند تسليم») of an order: how many came back signed (QR or marked by hand). */
export interface OrderDeliveryNotes { total: number; signed: number; state: 'WAITING' | 'PARTIAL' | 'SIGNED' }

/** One printed delivery note on the order page. */
export interface OrderNote {
  id: string;
  printedAt: string;
  items: Array<{ name: string; quantity: number }>;
  signed: boolean;
  files: number;
  lastUploadAt: string | null;
  drivers: string[];
  manualSignedAt: string | null;
  manualSignedBy: string | null;
}

export interface Order {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
  /** Newest order update, denormalised by the server (text is null for a workshop when it is staff-only). */
  latestUpdateAt?: string | null;
  latestUpdateKind?: string | null;
  latestUpdateText?: string | null;
  latestUpdateVisibility?: string | null;
  latestUpdateAuthor?: string | null;
  /** Delivery progress of the order's confirmed parts (orders list only). */
  delivery?: OrderDeliverySummary | null;
  /** Printed delivery notes and how many came back signed (orders list, staff only). */
  deliveryNotes?: OrderDeliveryNotes | null;
  /** «جدولة التسليم» — the planned day and trip (admins only; the delivery board). */
  deliverySchedule?: {
    day: string;
    groupId: string | null;
    driverName: string | null;
    driverNote: string | null;
    executedAt: string | null;
    missed: boolean;
  } | null;
  vehicle?: Vehicle;
  lineItems?: LineItem[];
  pendingReturnsCount?: number; // Count of items with pending return requests
  bids?: Bid[];
  customer?: Customer;
  attachments?: Attachment[];
  payments?: Payment[];
  transfers?: Transfer[];
  statusHistory?: OrderStatusHistory[];
  editHistory?: EditHistory[];
  totalAmount?: number;
  totalConfirmedAmount?: number; // Sum of confirmed, non-returned line items (admin list)
  claimedByAgentId?: string;
  receptionistId?: string;
  workshopName?: string;
  createdByUserId?: string;
  creator?: {
    id: string;
    username: string;
    role: UserRole;
  };
  claimedBy?: {
    id: string;
    username: string;
  };
  receptionist?: {
    id: string;
    name: string;
  };
  checklistSupplier?: {
    id: string;
    name: string;
    mapUrl?: string;
    phoneNumber?: string;
  };
  /** Which of the workshop's addresses these parts go to. Unset for the great
   *  majority of workshops, which receive at one place. */
  receivingLocationId?: string | null;
  receivingLocation?: WorkshopLocation | null;
  saleInvoiceUploaded?: boolean;
  purchaseInvoiceUploaded?: boolean;
  paymentReceived?: boolean;
  tabbyTamaraPricing?: boolean; // SUPER_ADMIN toggle: +10% Tabby/Tamara pricing
  readyForPickupAt?: string;
  // Per-item invoice tracking — derived markers
  awaitingPurchaseInvoice?: boolean; // exists confirmed item without PI match (and not bundled)
  awaitingSalesInvoice?: boolean;    // PI complete AND exists confirmed item not yet sales-billed
  purchaseInvoiceToPush?: boolean;   // a matched supplier bill not yet pushed to Daftra
  // Invoice indicator: skipped = excluded from invoice tracking (per-order flag
  // or before the cutoff date), yellow = PI missing, blue = all PIs in but sales
  // not done, green = all PIs in AND all items sales-invoiced. null = N/A.
  invoiceStatus?: 'skipped' | 'yellow' | 'blue' | 'green' | null;
  // Per-order "skip invoice tracking" flag (admin toggle).
  invoiceTrackingExcluded?: boolean;
  // Manual payment tracking (POS / Transfer / Cash / Tamara / Tabby)
  totalPaid?: number;
  totalDue?: number;
  paymentStatus?: 'NOT_PAID' | 'PARTIALLY_PAID' | 'FULLY_PAID';
  supplierStatus?: 'NOT_QUOTED' | 'QUOTED' | 'PURCHASE_REQUEST' | 'READY_FOR_PICKUP' | 'DELIVERED';
  customerProfile?: {
    id: string;
    name: string;
    phone: string;
    workshopName?: string | null;
    notes?: string | null;
  };
  vehicleProfile?: {
    id: string;
    carPlateNumber?: string | null;
    vin?: string | null;
    makeId: string;
    modelId: string;
    year: number;
    make?: { id: string; name: string };
    model?: { id: string; name: string };
  };
  // The order's workshop (type drives pricing/status; PRIVATE = "own" workshop).
  workshop?: {
    workshopType?: WorkshopType;
    fixedMarginPercentage?: number | null;
  };
}

export interface SupplierMaster {
  id: string;
  name: string;
  mapUrl?: string;
  phoneNumber?: string;
  iban?: string;
  isActive?: boolean;
}

export interface Vehicle {
  id: string;
  carPlateNumber?: string;
  vin: string;
  make?: { id: string; name: string };
  model?: { id: string; name: string };
  year: number;
}

export interface LineItem {
  id: string;
  partName: string;
  partNo?: string; // Part number (optional, extracted from quotation or entered manually)
  quantity: number;
  costPrice?: number;
  salePrice?: number;
  profitPercentage?: number;
  originalPrice?: number;
  finalPrice?: number;
  oemPrice?: number; // OEM cost (supplier price)
  aftermarketPrice?: number; // Aftermarket cost (supplier price)
  oemSalePrice?: number; // OEM with supplier margin (workshop sees this)
  aftermarketSalePrice?: number; // Aftermarket with supplier margin (workshop sees this)
  /** Delivery days per price: 0 = immediate «فوري», N = within N days, null = not stated. */
  oemLeadDays?: number | null;
  aftermarketLeadDays?: number | null;
  isUnavailable?: boolean;
  isConfirmed?: boolean; // Confirmed for PR (Purchase Request)
  confirmedPriceType?: 'OEM' | 'AFTERMARKET'; // Which price type was confirmed
  confirmedAt?: string;
  isReturned?: boolean;
  returnedAt?: string;
  returnRequestedAt?: string; // When return was requested
  returnApprovedAt?: string; // When return was approved by admin
  returnApprovedBy?: string; // Admin user ID who approved
  returnRejectedAt?: string; // When return was rejected
  returnRejectedBy?: string; // Admin user ID who rejected
  // Per-item return invoice (1:1) — set when admin uploads a return invoice
  // for this specific item via the order's invoice upload modal.
  returnInvoiceAttachmentId?: string;
  returnInvoiceMatchedAt?: string;
  reachedReadyForPickupAt?: string; // Set when order was READY_FOR_PICKUP and item was confirmed; once set, item cannot be unconfirmed
  readyEtaAt?: string | null; // Expected-ready time (now + N hours); drives the yellow "Nhr" badge
  receivedAt?: string | null; // Set when the workshop physically received this item
  receivedById?: string | null;
  /** How the receipt was recorded: RUN (a closed delivery run), MANUAL (this page), BASELINE (the opening cut). */
  receivedSource?: 'RUN' | 'MANUAL' | 'BASELINE' | null;
  /** The delivery run that carried (or is carrying) this item. */
  deliveryRunId?: string | null;
  dispatchedAt?: string | null;
  /** Why it came back from a run without being collected. */
  notCollectedReason?: string | null;
  deliveryRun?: { id: string; runNumber: string; driverName: string; status: 'OPEN' | 'CLOSED' | 'CANCELED'; createdAt: string; closedAt: string | null } | null;
  /** The signed sheet that proved it was handed over (admins only). */
  deliveryProof?: { id: string; files: Array<{ fileName: string; filePath: string; size: number; mimeType: string }>; uploadedAt: string } | null;
  /** Its delivery state, named server-side by the one rule: قيد التجهيز · جاهز ولم يُستلم · مع السائق · بلا ورقة موقعة · تم التسليم. */
  deliveryState?: 'PREPARING' | 'READY' | 'IN_TRANSIT' | 'UNPROVEN' | 'DELIVERED';
  /** While with the driver: when the signed sheet is due. */
  deliveryDueAt?: string | null;
  batchNo?: number | null; // Fulfillment batch (admin grouping)
  // Incumbent-first: a freshly added item only the order's current supplier may
  // price until this time (null = not exclusive). Admins can open it early.
  exclusiveUntil?: string | null;
  exclusiveOpenedAt?: string | null;
  selectedSupplierId?: string;
  selectedSupplier?: Supplier;
  workshopComment?: string; // Comment field for workshops
  wantsBothPrices?: boolean; // Workshop wants both OEM and Aftermarket prices on this item
  selectedPricing?: {
    id: string;
    costPrice: number;
    pricingMethod: string;
    setByAgentId: string;
    editHistory?: EditHistory[];
  };
  // ── Per-item invoice tracking ────────────────────────────────────────────
  // Purchase-invoice side
  purchaseInvoiceAttachmentId?: string | null;
  purchaseInvoicePrice?: number | null;
  purchaseInvoiceMatchedAt?: string | null;
  purchaseInvoiceMatchedById?: string | null;
  bundledUnderItem?: boolean; // marked as covered by another item's invoice line
  // Sales-invoice side (manual ERP submission)
  salesBilledAt?: string | null;
  salesBilledById?: string | null;
  salesBilledInvoiceRef?: string | null;
  // Per-item payment state (PaymentAllocation). Sent to internal roles only —
  // never to suppliers, since the allocated amount reveals the sale price.
  // null = the item isn't chargeable (unconfirmed / returned / unavailable).
  paidAmount?: number | null;
  unpaidAmount?: number | null;
  paymentLineTotal?: number | null;
}

export interface Bid {
  id: string;
  lineItemId: string;
  salePrice: number; // سعر البيع - what supplier enters
  costPrice: number; // Displayed price for workshops (salePrice + markup)
  originalPrice?: number; // For agreement customers
  timeToAvailableDays?: number; // Days until part is available
  comments?: string;
  isAccepted: boolean;
  submittedAt: string;
  distanceKm?: number | null; // From distance matrix (workshop ↔ supplier)
  supplier?: {
    id: string;
    name?: string;
    supplierProfile?: {
      name: string;
    };
  };
  editHistory?: EditHistory[];
}

export interface Customer {
  id: string;
  customerType: 'CASH' | 'COMPANY';
  isAgreementCustomer: boolean;
  discountPercentage?: number;
  customerName?: string;
  customerPhone?: string;
  companyName?: string;
}

export interface Supplier {
  id: string;
  name: string;
  mapUrl?: string;
  phoneNumber?: string;
  iban?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateOrderRequest {
  receptionistId?: string;
  workshopName?: string; // Admin/super-admin only: create the order on behalf of this workshop.
  customerProfileId?: string;
  vehicleProfileId?: string;
  vehicle: {
    carPlateNumber?: string;
    vin: string;
    /** How the VIN reached the form, so AI reads can be scored against the desk. */
    vinSource?: VinSource;
    makeId: string;
    modelId: string;
    year: number;
  };
  customer: {
    type?: 'CASH';
    customerName?: string;
    customerPhone?: string;
  };
  lineItems: Array<{
    partName: string;
    quantity: number;
    comment?: string;
    wantsBothPrices?: boolean;
  }>;
}

export interface SubmitQuoteRequest {
  lineItems: Array<{
    lineItemId: string;
    costPrice: number;
    costPriceSource: 'BID_ACCEPTED' | 'MANUAL_ENTRY';
    bidId?: string;
    selectedSupplierId?: string; // optional — backend accepts undefined (matches SubmitQuoteModal's own payload)
    pricingMethod: 'SALE_PRICE' | 'PROFIT_PERCENTAGE' | 'AGREEMENT_DISCOUNT';
    salePrice?: number;
    profitPercentage?: number;
    originalPrice?: number;
    // Fields the backend submitQuote already consumes (previously omitted from this FE type,
    // which is why SubmitQuoteModal had to pass an inferred object rather than an annotated one).
    oemPrice?: number;
    aftermarketPrice?: number;
    /** Delivery days per price (0 = immediate) — required for every price sent. */
    oemLeadDays?: number | null;
    aftermarketLeadDays?: number | null;
    partNo?: string;
    isUnavailable?: boolean;
  }>;
}

export interface PaymentRequest {
  paymentType: 'CASH' | 'POS' | 'PAYMENT_LINK' | 'CREDIT';
  amount: number;
  customerPhone?: string;
  paymentItems?: Array<{ lineItemId: string; unitPrice: number }>;
}



/**
 * One address a workshop receives parts at. Workshops with more than one must
 * have a location chosen on the order before supplier details go out — the
 * address is what the driver actually needs.
 */
export interface WorkshopLocation {
  id: string;
  workshopName: string;
  name: string;
  address?: string | null;
  locationUrl?: string | null;
  phoneNumber?: string | null;
  isActive: boolean;
  sortIndex: number;
  createdAt?: string;
}
